// Settings › Appointments (UC-APPOINTMENTS-001, 012; BR-APPOINTMENTS-008, -016; D23, D54, D63): the Zoho Bookings mapping — each
// branch to a workspace with its default type and its public booking page (D63: the address "Book in Zoho Bookings" and
// "Rebook" open; https only), each Bookings staff member to a user — from the live workspace listing when the
// secrets exist and from typed ids when they do not (A2), the appointment types — added, deactivated and reactivated here, each
// with the Bookings service names that import as it (UC-APPOINTMENTS-012) — the appointment-reminder switch (UC-APPOINTMENTS-014
// A8) and the run history. Owner and Admin (`appointments.type.write`); a Branch Owner without it sees the reminder switch for
// the branches they own and the no-access panel for the rest; anyone else sees the no-access panel.
import { APPOINTMENT_DURATION_MAX, APPOINTMENT_DURATION_MIN, BOOKING_PAGE_URL_MAX, BookingPageUrlSchema, foldServiceName, type AppointmentTypeInput, type AppointmentTypeView, type BookingsMapping, type BookingsMappingView, type BookingsWorkspacesView } from "@visionx/contracts";
import { useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState, type FormEvent, type ReactNode } from "react";
import { newIdempotencyKey } from "../api/client";
import { branches, settingStore, users } from "../api/endpoints";
import { appointmentTypes, bookings } from "../api/endpoints.appointments";
import { describeError, isApiError } from "../api/problem";
import { formatDateTime } from "../lib/format";
import { can, canEverywhere, useSessionView } from "../session/store";
import { WRITE_BLOCK_TITLE, useWriteBlock } from "../shell/OfflineBanner";
import { Alert, Checkbox, Chip, Empty, ErrorAlert, Field, Loading, Modal, Panel, Select, TextInput } from "../ui/bits";
import { Icon } from "../ui/icons";
import { NoAccessPanel, PageHead, SettingsTabs } from "./common";

type BranchRow = { branchId: string; branchName: string; branchCode: string; workspaceId: string; defaultTypeId: string; bookingPageUrl: string };
type StaffRow = { userId: string; displayName: string; role: string; staffId: string };

const rowsFrom = (view: BookingsMappingView | null, branchList: { id: string; name: string; code: string }[], userList: { id: string; displayName: string; role: string }[]) => ({
  branchRows: branchList.map((b): BranchRow => { const m = view?.branches.find((x) => x.branchId === b.id); return { branchId: b.id, branchName: b.name, branchCode: b.code, workspaceId: m?.workspaceId ?? "", defaultTypeId: m?.defaultTypeId ?? "", bookingPageUrl: m?.bookingPageUrl ?? "" }; }),
  staffRows: userList.map((u): StaffRow => ({ userId: u.id, displayName: u.displayName, role: u.role, staffId: view?.staff.find((x) => x.userId === u.id)?.staffId ?? "" })),
});

export function SettingsAppointments() {
  const s = useSessionView();
  const qc = useQueryClient();
  const block = useWriteBlock();
  const online = block === null;
  const mapping = useQuery({ queryKey: ["bookings-mapping"], queryFn: () => bookings.mapping() });
  const workspaces = useQuery({ queryKey: ["bookings-workspaces"], queryFn: () => bookings.workspaces(), enabled: mapping.isSuccess, retry: false });
  const branchList = useQuery({ queryKey: ["branches"], queryFn: () => branches.list(), enabled: mapping.isSuccess });
  const userList = useQuery({ queryKey: ["users", "active"], queryFn: () => users.list({ limit: 200, status: "Active" }), enabled: mapping.isSuccess });
  const types = useQuery({ queryKey: ["appointment-types"], queryFn: () => appointmentTypes.list(), enabled: mapping.isSuccess });
  const runs = useQuery({ queryKey: ["sync-runs", "all"], queryFn: () => bookings.syncRuns({ branchId: "all", limit: 20 }), enabled: mapping.isSuccess });
  const [branchRows, setBranchRows] = useState<BranchRow[]>([]);
  const [staffRows, setStaffRows] = useState<StaffRow[]>([]);
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<ReactNode | null>(null);
  const live: BookingsWorkspacesView | null = workspaces.data?.data ?? null;
  const notReady = workspaces.isError && isApiError(workspaces.error) && workspaces.error.code === "PROVIDER_NOT_READY";
  const typeRows = types.data?.data.items ?? [];

  useEffect(() => {
    if (!mapping.data || !branchList.data || !userList.data || dirty) return;
    const r = rowsFrom(mapping.data.data, branchList.data.data.items, userList.data.data.items);
    setBranchRows(r.branchRows);
    setStaffRows(r.staffRows);
  }, [mapping.data, branchList.data, userList.data, dirty]);

  // D63: the booking page is checked here with the contract's own rule (https, a host, no credentials) before the save.
  const pageError = (r: BranchRow): string | undefined => {
    if (!r.workspaceId.trim() || !r.bookingPageUrl.trim()) return undefined;
    const parsed = BookingPageUrlSchema.safeParse(r.bookingPageUrl.trim());
    return parsed.success ? undefined : r.bookingPageUrl.trim().toLowerCase().startsWith("http://") ? "The booking page must start https://." : "The branch's Zoho Bookings booking-page address, starting https://.";
  };
  const invalid = branchRows.some((r) => pageError(r) !== undefined);
  const save = async () => {
    if (invalid) return;
    setBusy(true);
    setNotice(null);
    const body: BookingsMapping = {
      branches: branchRows.filter((r) => r.workspaceId.trim()).map((r) => ({ branchId: r.branchId as never, workspaceId: r.workspaceId.trim(), defaultTypeId: (r.defaultTypeId || null) as never, bookingPageUrl: r.bookingPageUrl.trim() || null })),
      staff: staffRows.filter((r) => r.staffId.trim()).map((r) => ({ userId: r.userId as never, staffId: r.staffId.trim() })),
    };
    try {
      await bookings.putMapping(body, newIdempotencyKey());
      setDirty(false);
      setNotice(<Alert tone="ok"><b>Mapping saved.</b> Mapped branches pull from the next run; unmapped ones are skipped.</Alert>);
      void qc.invalidateQueries({ queryKey: ["bookings-mapping"] });
    } catch (err) {
      setNotice(<Alert tone="crit"><b>Could not save.</b> {describeError(err)}</Alert>);
    } finally {
      setBusy(false);
    }
  };
  const staffOf = (workspaceId: string) => live?.workspaces.find((w) => w.id === workspaceId)?.staff ?? [];
  const allStaff = live ? live.workspaces.flatMap((w) => w.staff.map((st) => ({ ...st, workspace: w.name }))) : [];
  const lastRun = (branchId: string) => mapping.data?.data.lastRuns.find((r) => r.branchId === branchId) ?? null;

  return (
    <section className="screen" id="s-settings-appointments">
      <div className="content">
        <PageHead title="Appointments" sub="Zoho Bookings owns the slot; Vision X reads each branch's workspace every five minutes (D23, D53)" actions={
          mapping.data && <button className="btn primary" type="button" disabled={!online || busy || !dirty || invalid} title={block ? WRITE_BLOCK_TITLE[block] : invalid ? "Correct the booking page first" : undefined} onClick={() => void save()}>{busy ? "Saving…" : "Save mapping"}</button>
        } />
        <SettingsTabs />
        {notice}
        {mapping.isPending && <Loading what="Bookings mapping" />}
        {mapping.isError && (isApiError(mapping.error) && mapping.error.code === "PERMISSION_DENIED"
          // A Branch Owner may still switch reminders for the branches they own (UC-APPOINTMENTS-014 A8, D40).
          ? (can(s, "tenancy.settings.write") ? <><RemindersPanel block={block} /><NoAccessPanel page="the Bookings mapping and appointment types" error={mapping.error} /></> : <NoAccessPanel page="Appointments settings" error={mapping.error} />)
          : <ErrorAlert error={mapping.error} />)}
        {mapping.data && (
          <>
            {notReady && <Alert tone="warn"><b>Zoho Bookings is not connected yet</b>{isApiError(workspaces.error) && Array.isArray(workspaces.error.meta.missing) ? ` — missing ${workspaces.error.meta.missing.map(String).join(", ")}` : ""}. Type the workspace and staff ids from Zoho; the live list appears once the platform operator sets the secrets (D35).</Alert>}
            {workspaces.isError && !notReady && <ErrorAlert error={workspaces.error} />}
            <div className="split">
              <Panel title="Branches and workspaces" meta={live ? `${live.workspaces.length} workspaces live · fetched ${formatDateTime(live.fetchedAt)}` : "one workspace per branch"} tight
                foot={<span className="help">The booking page is the branch's public Zoho Bookings address; "Book in Zoho Bookings" and "Rebook" open it, and with none set no Bookings link shows.</span>}>
                <div className="tbl-scroll">
                  <table>
                    <thead><tr><th>Branch</th><th>Bookings workspace</th><th>Booking page</th><th>Unmapped services import as</th><th>Last pull</th></tr></thead>
                    <tbody>
                      {branchRows.map((r) => {
                        const run = lastRun(r.branchId);
                        return (
                          <tr key={r.branchId}>
                            <td>{r.branchName}<div className="small mono faint">{r.branchCode}</div></td>
                            <td>
                              {live ? (
                                <Select aria-label={`Workspace for ${r.branchName}`} value={r.workspaceId} onChange={(e) => { setDirty(true); setBranchRows((rows) => rows.map((x) => (x.branchId === r.branchId ? { ...x, workspaceId: e.target.value } : x))); }}>
                                  <option value="">Not mapped</option>
                                  {live.workspaces.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
                                  {r.workspaceId && !live.workspaces.some((w) => w.id === r.workspaceId) && <option value={r.workspaceId}>{r.workspaceId}</option>}
                                </Select>
                              ) : (
                                <TextInput aria-label={`Workspace id for ${r.branchName}`} value={r.workspaceId} placeholder="workspace id" mono onChange={(e) => { setDirty(true); setBranchRows((rows) => rows.map((x) => (x.branchId === r.branchId ? { ...x, workspaceId: e.target.value } : x))); }} />
                              )}
                            </td>
                            <td>
                              <div className="field">
                              <TextInput aria-label={`Booking page for ${r.branchName}`} type="url" inputMode="url" autoComplete="off" spellCheck={false} maxLength={BOOKING_PAGE_URL_MAX} value={r.bookingPageUrl}
                                placeholder={r.workspaceId ? "https://…zohobookings.com/…" : "needs a workspace"} disabled={!r.workspaceId.trim()} error={pageError(r) !== undefined}
                                aria-invalid={pageError(r) !== undefined} aria-describedby={pageError(r) ? `page-err-${r.branchId}` : undefined}
                                onChange={(e) => { setDirty(true); setBranchRows((rows) => rows.map((x) => (x.branchId === r.branchId ? { ...x, bookingPageUrl: e.target.value } : x))); }} />
                              {pageError(r) && <div className="error" id={`page-err-${r.branchId}`} role="alert">{pageError(r)}</div>}
                              </div>
                            </td>
                            <td>
                              <Select aria-label={`Default type for ${r.branchName}`} value={r.defaultTypeId} onChange={(e) => { setDirty(true); setBranchRows((rows) => rows.map((x) => (x.branchId === r.branchId ? { ...x, defaultTypeId: e.target.value } : x))); }}>
                                <option value="">Follow-up (default)</option>
                                {typeRows.filter((t) => t.isActive).map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                              </Select>
                            </td>
                            <td>{run ? <><div className="nw">{run.finishedAt ? formatDateTime(run.finishedAt) : "running"}</div><div className="small faint">{run.outcome}</div></> : <span className="faint">never</span>}</td>
                          </tr>
                        );
                      })}
                      {branchRows.length === 0 && <tr><td colSpan={5}><span className="help">{branchList.isPending ? "Loading branches…" : "No branches."}</span></td></tr>}
                    </tbody>
                  </table>
                </div>
              </Panel>
              <Panel title="Staff" meta="Bookings staff member → user" tight foot={<span className="help">An unpaired staff member's bookings import unassigned; pairing later fills them in on the next pull.</span>}>
                <div className="tbl-scroll">
                  <table>
                    <thead><tr><th>User</th><th>Bookings staff</th></tr></thead>
                    <tbody>
                      {staffRows.map((r) => (
                        <tr key={r.userId}>
                          <td>{r.displayName}<div className="small faint">{r.role}</div></td>
                          <td>
                            {live ? (
                              <Select aria-label={`Bookings staff for ${r.displayName}`} value={r.staffId} onChange={(e) => { setDirty(true); setStaffRows((rows) => rows.map((x) => (x.userId === r.userId ? { ...x, staffId: e.target.value } : x))); }}>
                                <option value="">Not paired</option>
                                {(branchRows.some((b) => b.workspaceId) ? branchRows.flatMap((b) => staffOf(b.workspaceId)) : allStaff).filter((st, i, arr) => arr.findIndex((y) => y.id === st.id) === i).map((st) => <option key={st.id} value={st.id}>{st.name}{st.email ? ` · ${st.email}` : ""}{st.suggestedUserId === r.userId ? " · suggested" : ""}</option>)}
                                {r.staffId && !allStaff.some((st) => st.id === r.staffId) && <option value={r.staffId}>{r.staffId}</option>}
                              </Select>
                            ) : (
                              <TextInput aria-label={`Bookings staff id for ${r.displayName}`} value={r.staffId} placeholder="staff id" mono onChange={(e) => { setDirty(true); setStaffRows((rows) => rows.map((x) => (x.userId === r.userId ? { ...x, staffId: e.target.value } : x))); }} />
                            )}
                          </td>
                        </tr>
                      ))}
                      {staffRows.length === 0 && <tr><td colSpan={2}><span className="help">{userList.isPending ? "Loading users…" : "No active users."}</span></td></tr>}
                    </tbody>
                  </table>
                </div>
              </Panel>
            </div>
            <TypesPanel types={typeRows} live={live} online={online} />
            {can(s, "tenancy.settings.read") && <RemindersPanel block={block} />}
            <Panel title="Pull history" meta="newest first" tight>
              {runs.isPending && <Loading what="runs" />}
              {runs.isError && <div className="panel-body"><ErrorAlert error={runs.error} /></div>}
              {runs.data && (runs.data.data.items.length === 0 ? <Empty title="No pull has run yet." /> : (
                <div className="tbl-scroll">
                  <table>
                    <thead><tr><th>Started</th><th>Branch</th><th>Trigger</th><th>Window</th><th>Outcome</th><th className="r">Fetched</th><th className="r">New</th><th className="r">Updated</th><th className="r">To match</th><th className="r">Conflicts</th><th>Error</th></tr></thead>
                    <tbody>
                      {runs.data.data.items.map((r) => (
                        <tr key={r.id}>
                          <td className="nw">{formatDateTime(r.startedAt)}</td>
                          <td>{branchRows.find((b) => b.branchId === r.branchId)?.branchName ?? <span className="mono faint">{r.branchId}</span>}</td>
                          <td>{r.trigger}{r.startedBy ? ` · ${r.startedBy.displayName}` : ""}</td>
                          <td>{r.windowKind}</td>
                          <td><Chip tone={r.outcome === "Succeeded" ? "ok" : r.outcome === "Failed" ? "crit" : r.outcome === "Running" ? "info" : "neutral"} dot>{r.outcome}</Chip></td>
                          <td className="r num">{r.counts.fetched}</td><td className="r num">{r.counts.created}</td><td className="r num">{r.counts.updated}</td><td className="r num">{r.counts.inbox}</td><td className="r num">{r.counts.conflicts}</td>
                          <td className="small faint">{r.errorText ?? ""}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ))}
            </Panel>
          </>
        )}
      </div>
    </section>
  );
}

const REMINDERS = "appointmentRemindersEnabled";
/** A branch row's choice: its own value, or none — it follows the organisation. */
type ReminderChoice = "on" | "off" | "organisation";

/**
 * Appointment reminders (UC-APPOINTMENTS-014 A8; BR-APPOINTMENTS-018): whether Vision X queues the 24-hour email reminder — the
 * organisation's value and each active branch's own, through the setting store (UC-TENANCY-007). The Owner changes any of them,
 * a Branch Owner the branches they own (D40), an Admin reads them; each Save is one audited setting change, and the rows not
 * yet due follow on the branch's next pull (A6).
 */
function RemindersPanel({ block }: { block: ReturnType<typeof useWriteBlock> }) {
  const s = useSessionView();
  const qc = useQueryClient();
  const everywhere = canEverywhere(s, "tenancy.settings.write");
  const owned = new Set<string>(s?.branchScopedPermissions.find((p) => p.code === "tenancy.settings.write")?.branchIds ?? []);
  const mayChange = (scope: string): boolean => everywhere || (scope !== "org" && owned.has(scope));
  const branchList = useQuery({ queryKey: ["branches"], queryFn: () => branches.list() });
  const active = (branchList.data?.data.items ?? []).filter((b) => b.status === "Active");
  const org = useQuery({ queryKey: ["settings", "org", "messaging"], queryFn: () => settingStore.read("org", "messaging") });
  const perBranch = useQueries({ queries: active.map((b) => ({ queryKey: ["settings", b.id, "messaging"], queryFn: () => settingStore.read(b.id, "messaging") })) });
  const [drafts, setDrafts] = useState<Record<string, ReminderChoice>>({});
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [notice, setNotice] = useState<ReactNode | null>(null);
  const orgHeld = org.data?.data.settings[REMINDERS];
  const orgOn = orgHeld?.value === true;
  const onOff = (on: boolean) => (on ? "On" : "Off");
  const save = async (scope: string, name: string, choice: ReminderChoice) => {
    setBusy(scope);
    setError(null);
    setNotice(null);
    try {
      if (choice === "organisation") await settingStore.removeOverride(scope, "messaging", REMINDERS);
      else await settingStore.save(scope, "messaging", { [REMINDERS]: choice === "on" });
      setDrafts((d) => { const { [scope]: _saved, ...rest } = d; return rest; });
      setNotice(<Alert tone="ok"><b>{choice === "organisation" ? `${name} follows the organisation again.` : `Reminders ${choice} for ${name}.`}</b> Appointments already pulled follow on {scope === "org" ? "each branch's" : "the branch's"} next pull.</Alert>);
      await qc.invalidateQueries({ queryKey: ["settings"] });
    } catch (err) { setError(err); }
    finally { setBusy(null); }
  };
  const saveButton = (scope: string, name: string, current: ReminderChoice) => {
    const draft = drafts[scope];
    const changed = draft !== undefined && draft !== current;
    return <button className="btn sm" type="button" aria-label={`Save reminders for ${name}`} disabled={block !== null || !changed || busy !== null} title={block ? WRITE_BLOCK_TITLE[block] : undefined} onClick={() => void save(scope, name, draft!)}>{busy === scope ? "Saving…" : "Save"}</button>;
  };
  return (
    <Panel title="Appointment reminders" meta="24-hour email reminder · off unless switched on" tight
      foot={<span className="help">Zoho Bookings also reminds patients: switch Vision X’s reminders on only if Vision X should remind instead. Every change is recorded in the audit log.</span>}>
      {notice && <div className="panel-body" aria-live="polite">{notice}</div>}
      {error !== null && <div className="panel-body"><ErrorAlert error={error} /></div>}
      {org.isPending && <Loading what="reminder settings" />}
      {org.isError && <div className="panel-body"><ErrorAlert error={org.error} /></div>}
      {org.data && (
        <div className="tbl-scroll">
          <table>
            <thead><tr><th>Applies to</th><th>Reminders</th><th></th></tr></thead>
            <tbody>
              <tr>
                <td>Organisation<div className="small faint">every branch without its own setting</div></td>
                <td>
                  {mayChange("org") ? (
                    <Select aria-label="Reminders for the organisation" value={drafts.org ?? (orgOn ? "on" : "off")} onChange={(e) => setDrafts((d) => ({ ...d, org: e.target.value as ReminderChoice }))}>
                      <option value="off">Off</option>
                      <option value="on">On</option>
                    </Select>
                  ) : onOff(orgOn)}
                  {orgHeld?.source === "default" && <div className="small faint">the default</div>}
                </td>
                <td className="r nw">{mayChange("org") && saveButton("org", "the organisation", orgOn ? "on" : "off")}</td>
              </tr>
              {active.map((b, i) => {
                const q = perBranch[i];
                const held = q?.data?.data.settings[REMINDERS];
                const current: ReminderChoice = held?.source === "branch" ? (held.value === true ? "on" : "off") : "organisation";
                return (
                  <tr key={b.id}>
                    <td>{b.name}<div className="small mono faint">{b.code}</div></td>
                    <td>
                      {q?.isError ? <ErrorAlert error={q.error} /> : !held ? <span className="faint">Loading…</span> : mayChange(b.id) ? (
                        <Select aria-label={`Reminders for ${b.name}`} value={drafts[b.id] ?? current} onChange={(e) => setDrafts((d) => ({ ...d, [b.id]: e.target.value as ReminderChoice }))}>
                          <option value="organisation">As the organisation ({onOff(orgOn)})</option>
                          <option value="on">On at this branch</option>
                          <option value="off">Off at this branch</option>
                        </Select>
                      ) : <>{onOff(held.value === true)}<div className="small faint">{current === "organisation" ? "as the organisation" : "this branch’s own setting"}</div></>}
                    </td>
                    <td className="r nw">{held && mayChange(b.id) && saveButton(b.id, b.name, current)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </Panel>
  );
}

/**
 * Appointment types with the Bookings service names that import as them (BR-APPOINTMENTS-016; UC-APPOINTMENTS-012): Add type
 * (step 4), each row's service names (step 2) and Deactivate / Reactivate (A1) — a type in use is never deleted, and a
 * deactivated type leaves the pickers while its appointments keep it.
 */
function TypesPanel({ types, live, online }: { types: AppointmentTypeView[]; live: BookingsWorkspacesView | null; online: boolean }) {
  const qc = useQueryClient();
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [adding, setAdding] = useState(false);
  const [notice, setNotice] = useState<ReactNode | null>(null);
  const services = live ? [...new Set(live.workspaces.flatMap((w) => w.services.map((sv) => sv.name)))] : [];
  const setActive = async (t: AppointmentTypeView, isActive: boolean) => {
    setBusy(t.id);
    setError(null);
    setNotice(null);
    try {
      await appointmentTypes.patch(t.id, { isActive });
      setNotice(<Alert tone={isActive ? "ok" : "info"}><b>{t.name} {isActive ? "reactivated" : "deactivated"}.</b> {isActive ? "It is offered again and its service names match from the next pull." : "It leaves the pickers; its appointments keep it, and its services import as the branch's default type until it is reactivated."}</Alert>);
      void qc.invalidateQueries({ queryKey: ["appointment-types"] });
    } catch (err) { setError(err); }
    finally { setBusy(null); }
  };
  const save = async (t: AppointmentTypeView) => {
    const names = (drafts[t.id] ?? "").split(",").map((x) => x.trim()).filter(Boolean);
    setBusy(t.id);
    setError(null);
    try {
      await appointmentTypes.patch(t.id, { serviceNames: names });
      setDrafts((d) => { const { [t.id]: _gone, ...rest } = d; return rest; });
      void qc.invalidateQueries({ queryKey: ["appointment-types"] });
    } catch (err) { setError(err); }
    finally { setBusy(null); }
  };
  return (
    <Panel title="Appointment types" meta="which Bookings service imports as which type" tight
      head={<button className="btn sm right" type="button" disabled={!online} title={online ? undefined : "Unavailable offline"} onClick={() => { setNotice(null); setAdding(true); }}><Icon name="plus" />Add type</button>}
      foot={<span className="help">{services.length > 0 ? `Services in Bookings: ${services.join(" · ")}` : "A service with no mapped name imports as the branch's default type and is marked unmapped."}</span>}>
      {notice && <div className="panel-body">{notice}</div>}
      {error !== null && <div className="panel-body"><ErrorAlert error={error} /></div>}
      {types.length === 0 ? <Empty title="No appointment types yet." /> : (
        <div className="tbl-scroll">
          <table>
            <thead><tr><th>Type</th><th>Bookings service names</th><th></th></tr></thead>
            <tbody>
              {types.map((t) => {
                const draft = drafts[t.id];
                return (
                  <tr key={t.id}>
                    {/* The type's settings sit under its name, so the service names and the row's actions fit a tablet's width. */}
                    <td>
                      <div className="nw">{t.colourHex && <span aria-hidden="true" style={{ display: "inline-block", width: 10, height: 10, borderRadius: 2, background: t.colourHex, marginRight: 6, verticalAlign: "middle" }} />}{t.name} <span className="mono faint">{t.code}</span>{!t.isActive && <> <Chip tone="neutral">Inactive</Chip></>}</div>
                      <div className="small faint"><span className="num nw">{t.defaultDurationMinutes} min</span> · <span className="nw">{t.isRecallSource && t.recallIntervalMonths ? `recall ${t.recallIntervalMonths} months` : "no recall"}</span> · <span className="nw">{t.requiresOptometrist ? "optometrist required" : "no optometrist needed"}</span></div>
                    </td>
                    <td style={{ width: "100%" }}><TextInput aria-label={`Service names for ${t.name}`} value={draft ?? t.serviceNames.join(", ")} placeholder="comma-separated" disabled={!t.isActive} onChange={(e) => setDrafts((d) => ({ ...d, [t.id]: e.target.value }))} /></td>
                    <td className="r nw">
                      <span className="row c gap-4">
                        <button className="btn sm" type="button" disabled={!online || draft === undefined || busy === t.id} onClick={() => void save(t)}>{busy === t.id && draft !== undefined ? "Saving…" : "Save"}</button>
                        {t.isActive
                          ? <button className="btn sm ghost" type="button" disabled={!online || busy === t.id} title={`Stop offering ${t.name}; its appointments keep it`} onClick={() => void setActive(t, false)}>Deactivate</button>
                          : <button className="btn sm" type="button" disabled={!online || busy === t.id} onClick={() => void setActive(t, true)}>Reactivate</button>}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {adding && <AddTypeModal types={types} services={services} onClose={() => setAdding(false)} onAdded={(t) => {
        setAdding(false);
        setNotice(<Alert tone="ok"><b>{t.name} added.</b> {t.serviceNames.length > 0 ? `Bookings services named ${t.serviceNames.join(", ")} import as it from the next pull.` : "Name its Bookings services here so pulled bookings land on it."}</Alert>);
        void qc.invalidateQueries({ queryKey: ["appointment-types"] });
      }} />}
    </Panel>
  );
}

/** A type code from its name: `Contact lens aftercare` → `contact_lens_aftercare` (the contract's `^[a-z][a-z0-9_]{1,39}$`). */
export function codeFromName(name: string): string {
  const folded = name.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "").slice(0, 40).replace(/_+$/, "");
  return /^[a-z]/.test(folded) ? folded : folded ? `t_${folded}`.slice(0, 40) : "";
}
const CODE = /^[a-z][a-z0-9_]{1,39}$/;
const HEX = /^#[0-9A-Fa-f]{6}$/;

/**
 * Add type (UC-APPOINTMENTS-012 step 4): name, a code derived from it (fixed once created), default duration, colour, whether an
 * optometrist is needed, a recall interval — which makes the type a recall source, as the eye test and contact lenses are — and
 * the Bookings service names. `POST /appointment-types` with an Idempotency-Key; a refused field is named at its box (E1).
 */
/**
 * Whether an API field error belongs to a form field: the field itself, or an item of it in either notation the API uses — the
 * service's `serviceNames[0]` or the contract's own refinement, joined with dots by the validation hook (`serviceNames.1`).
 */
const onPath = (errorPath: string, field: string): boolean => errorPath === field || errorPath.startsWith(`${field}[`) || errorPath.startsWith(`${field}.`);

function AddTypeModal({ types, services, onClose, onAdded }: { types: AppointmentTypeView[]; services: string[]; onClose: () => void; onAdded: (t: AppointmentTypeView) => void }) {
  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [codeTouched, setCodeTouched] = useState(false);
  const [duration, setDuration] = useState("30");
  const [colour, setColour] = useState("");
  const [optometrist, setOptometrist] = useState(true);
  const [recall, setRecall] = useState("");
  const [names, setNames] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  // One Idempotency-Key per distinct body: a retry of the same body replays; a corrected body is a new request.
  const [keyed, setKeyed] = useState<{ body: string; key: string } | null>(null);
  const effectiveCode = codeTouched ? code.trim() : codeFromName(name);
  const minutes = Number(duration);
  const months = recall.trim() === "" ? null : Number(recall);
  const serviceNames = names.split(",").map((x) => x.trim()).filter(Boolean);
  const taken = new Set(types.flatMap((t) => t.serviceNames.map(foldServiceName)));
  const folded = serviceNames.map(foldServiceName);
  const repeated = serviceNames.find((_, i) => folded.indexOf(folded[i]!) !== i);
  const onAnotherType = serviceNames.find((n) => taken.has(foldServiceName(n)));
  const local: Record<string, string | undefined> = {
    code: effectiveCode && !CODE.test(effectiveCode) ? "Lower-case letters, digits and _ , starting with a letter (2–40)." : types.some((t) => t.code === effectiveCode) ? `${effectiveCode} is already a type's code.` : undefined,
    defaultDurationMinutes: duration && (!Number.isInteger(minutes) || minutes < APPOINTMENT_DURATION_MIN || minutes > APPOINTMENT_DURATION_MAX) ? `Between ${APPOINTMENT_DURATION_MIN} and ${APPOINTMENT_DURATION_MAX} minutes.` : undefined,
    colourHex: colour.trim() && !HEX.test(colour.trim()) ? "A colour as #RRGGBB, for example #2F6FED." : undefined,
    recallIntervalMonths: months !== null && (!Number.isInteger(months) || months < 1 || months > 120) ? "Between 1 and 120 months, or empty for none." : undefined,
    serviceNames: repeated !== undefined ? `"${serviceNames[folded.indexOf(foldServiceName(repeated))]}" is listed twice.` : onAnotherType !== undefined ? `"${onAnotherType}" already imports as another type.` : undefined,
  };
  const apiErr = (path: string) => (isApiError(error) ? (error.errors.find((f) => onPath(f.path, path))?.message) : undefined);
  const err = (path: string) => local[path] ?? apiErr(path);
  const ready = name.trim().length > 0 && !!effectiveCode && duration !== "" && Object.values(local).every((v) => v === undefined) && !busy;
  const shown = ["name", "code", "defaultDurationMinutes", "colourHex", "recallIntervalMonths", "serviceNames"];
  const submit = async (e?: FormEvent) => {
    e?.preventDefault();
    if (!ready) return;
    const body: AppointmentTypeInput = {
      code: effectiveCode, name: name.trim(), defaultDurationMinutes: minutes, requiresOptometrist: optometrist,
      ...(colour.trim() ? { colourHex: colour.trim().toUpperCase() } : {}),
      recallIntervalMonths: months, isRecallSource: months !== null,
      ...(serviceNames.length > 0 ? { serviceNames } : {}),
    };
    const json = JSON.stringify(body);
    const key = keyed && keyed.body === json ? keyed.key : newIdempotencyKey();
    setKeyed({ body: json, key });
    setBusy(true);
    setError(null);
    try {
      const { data } = await appointmentTypes.create(body, key);
      onAdded(data);
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal title="Add appointment type" meta="the code is fixed once created" wide onClose={onClose} onSubmit={(e) => void submit(e)} footer={<>
      <button className="btn" type="button" onClick={onClose}>Cancel</button>
      <button className="btn primary" type="submit" disabled={!ready}>{busy ? "Adding…" : "Add type"}</button>
    </>}>
      {error !== null && !(isApiError(error) && error.code === "VALIDATION_FAILED" && error.errors.length > 0 && error.errors.every((f) => shown.some((p) => onPath(f.path, p)))) && <Alert tone="crit"><b>Could not add the type.</b> {describeError(error)}</Alert>}
      <div className="grid-2">
        <Field label="Name" required htmlFor="type-name" error={apiErr("name")}><TextInput id="type-name" value={name} maxLength={80} autoFocus onChange={(e) => setName(e.target.value)} placeholder="Contact lens aftercare" /></Field>
        <Field label="Code" required htmlFor="type-code" error={err("code")} help="Derived from the name; used by imports and reports."><TextInput id="type-code" value={effectiveCode} maxLength={40} mono onChange={(e) => { setCodeTouched(true); setCode(e.target.value); }} placeholder="cl_aftercare" error={!!err("code")} /></Field>
        <Field label="Default duration (minutes)" required htmlFor="type-duration" error={err("defaultDurationMinutes")}><TextInput id="type-duration" type="number" inputMode="numeric" min={APPOINTMENT_DURATION_MIN} max={APPOINTMENT_DURATION_MAX} step={5} value={duration} mono onChange={(e) => setDuration(e.target.value)} error={!!err("defaultDurationMinutes")} /></Field>
        <Field label="Colour" htmlFor="type-colour" error={err("colourHex")} help="Optional, as #RRGGBB.">
          <TextInput id="type-colour" value={colour} maxLength={7} mono onChange={(e) => setColour(e.target.value)} placeholder="#2F6FED" error={!!err("colourHex")} />
        </Field>
        <Field label="Recall interval (months)" htmlFor="type-recall" error={err("recallIntervalMonths")} help="Completing it sets the patient's recall due date. Empty for none."><TextInput id="type-recall" type="number" inputMode="numeric" min={1} max={120} value={recall} mono onChange={(e) => setRecall(e.target.value)} placeholder="none" error={!!err("recallIntervalMonths")} /></Field>
        <Field label="Optometrist"><Checkbox checked={optometrist} onChange={setOptometrist}>Needs an optometrist</Checkbox></Field>
      </div>
      <Field label="Bookings service names" htmlFor="type-services" error={err("serviceNames")} help={services.length > 0 ? `Comma-separated. Services in Bookings: ${services.join(" · ")}` : "Comma-separated; each name imports as one type only."}>
        <TextInput id="type-services" value={names} onChange={(e) => setNames(e.target.value)} placeholder="CL aftercare" error={!!err("serviceNames")} />
      </Field>
    </Modal>
  );
}
